/**
 * Security helpers shared by the whole frontend.
 */

// Allow-list of safe external destinations for outbound CTAs.
const SAFE_HOSTS = new Set([
  "wa.me",
  "api.whatsapp.com",
  "mailto",
  "protect-hub.com",
  "www.protect-hub.com",
  "linkedin.com",
  "www.linkedin.com",
  "instagram.com",
  "www.instagram.com",
]);

/**
 * Build a sanitized external URL.
 *
 * `url` MUST resolve to an absolute http(s) or mailto: URL whose host is
 * present in {@link SAFE_HOSTS}. Anything else is rejected and an empty
 * "#" fragment is returned – this prevents Open Redirection through CTA
 * props that may have been tampered with via DOM injection.
 */
export function safeExternalUrl(url) {
  if (typeof url !== "string" || url.length === 0) return "#";

  if (url.startsWith("mailto:")) {
    const email = url.slice(7).trim();
    if (/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) {
      return `mailto:${email}`;
    }
    return "#";
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return "#";
    if (!SAFE_HOSTS.has(parsed.host)) return "#";
    return parsed.toString();
  } catch {
    return "#";
  }
}

/** Standard secure attributes for any `target="_blank"` link. */
export const SAFE_BLANK = {
  target: "_blank",
  rel: "noopener noreferrer",
};

/**
 * Throttle helper: ensures `fn` runs at most once every `wait` ms.
 * Used for click handlers to mitigate client-side DoS through automated
 * mass-clicks.
 */
export function throttle(fn, wait = 800) {
  let last = 0;
  let timer = null;
  return (...args) => {
    const now = Date.now();
    const remaining = wait - (now - last);
    if (remaining <= 0) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      last = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        last = Date.now();
        timer = null;
        fn(...args);
      }, remaining);
    }
  };
}

/**
 * Allow-list of valid tab IDs. Any attempt to assign a tab outside this
 * list is treated as Parameter Tampering and ignored.
 */
export const VALID_TABS = Object.freeze(["enterprise", "segura", "contact", "legal"]);

export function sanitizeTab(tab) {
  return VALID_TABS.includes(tab) ? tab : "enterprise";
}

/** Strip dangerous control characters that may slip into text inputs. */
export function sanitizeText(value) {
  if (typeof value !== "string") return "";
  return value.replace(/[\u0000-\u001F\u007F]/g, "").trim();
}
